import { useMemo, useState } from 'react';

import { toast } from 'sonner';

import { useGetDocFacts } from '@/api/queries/useGetDocFacts';
import { useGetFact } from '@/api/queries/useGetFact';
import PDFViewer from '@/components/organisms/PDFViewer';
import { useDocumentNavigation } from '@/hooks/useOpenDocViewer';
import { processFactHighlights } from '@/new/lib/doc-viewer-utils';
import { useStore } from '@/store';

export const FactDocViewer = ({
  caseId,
  docId,
  factId,
  onClose,
  hideFeatureButtons,
  hideCloseButton,
}: {
  caseId: string | null;
  docId: string;
  factId?: string | null;
  onClose?: () => void;
  hideFeatureButtons?: boolean;
  hideCloseButton?: boolean;
}) => {
  const { closeDocViewer } = useDocumentNavigation();
  const setPopupFact = useStore((state) => state.setPopupFact);

  const [showFacts, setShowFacts] = useState(false);

  // Data fetching
  const { data: fact } = useGetFact(caseId, factId);
  const { data: documentFacts } = useGetDocFacts({ docId, caseId });

  const { coordinates } = useMemo(() => {
    if (!fact) return { coordinates: [] };
    if (showFacts) {
      if (!documentFacts) return { coordinates: [] };
      return processFactHighlights(documentFacts, docId);
    }
    return processFactHighlights([fact], docId);
  }, [fact, documentFacts, docId, showFacts]);

  const handleHighlightClick = (id: string) => {
    if (factId) {
      const fact = documentFacts?.find((fact) => fact.id === id);
      if (fact) {
        setPopupFact(fact);
        // Copy source text to clipboard, find the source text for that docId and factId
        const location = fact.locations.find((location) => location.docId === docId);
        if (location) {
          navigator.clipboard.writeText(location?.sources?.map((source) => source.text).join('\n') ?? '');
          toast.success('Source text copied to clipboard');
        }
      }
    }
  };

  if (!caseId) {
    return null;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-grow overflow-hidden">
        <div className="relative h-full">
          <PDFViewer
            caseId={caseId}
            docId={docId}
            highlights={coordinates}
            closeViewer={hideCloseButton ? undefined : (onClose ?? closeDocViewer)}
            highlightedFactId={factId}
            onHighlightClick={handleHighlightClick}
            hideFeatureButtons={hideFeatureButtons}
            showFacts={showFacts}
            setShowFacts={setShowFacts}
          />
        </div>
      </div>
    </div>
  );
};
